import { useValidate } from '@flywire/react-hooks';
import { CHINA_COUNTRY_CODE } from '@packages/domain/constants/countries';
import { validateSectionField } from 'actions/entities/sections/sections';
import { fetchSender } from 'actions/entities/sender/sender';
import { setCaptchaResponse } from 'actions/ui/captcha/captcha';
import {
  setFieldValue,
  setShouldDisablePayerInformationFields,
  updateOrderTemplate,
} from 'actions/ui/ui';
import classNames from 'classnames';
import { BackButton } from 'components/BackButton/BackButton';
import { ErrorComponent } from 'components/Error/Error';
import {
  Captcha,
  type CaptchaRef,
} from 'components/thirdParty/Captcha/Captcha';
import { Button } from 'components/ui/Button/Button';
import { Heading } from 'components/ui/Heading/Heading';
import { Spinner } from 'components/ui/Spinner';
import { SENDER_COUNTRY_FIELD } from 'constants/fields';
import { useModalContext } from 'hooks/useModalContext';
import { usePaymentCreation } from 'hooks/usePaymentCreation/usePaymentCreation';
import { ReactComponent as ExclamationIcon } from 'icons/exclamation-red.svg';
import type { Field as FieldModel, FieldValue } from 'models';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'reducers/types';
import { getFieldById } from 'selectors/entities/fields/fields';
import { transformChinaFields } from 'selectors/entities/sections/china/sections';
import { getOrderTemplate } from 'selectors/orderTemplate/orderTemplate';
import {
  getCurrentCountryCode,
  getPayerInformationCountrySuggestion,
  getReadOnlyFields,
  isFetching,
  payerInformationEditableFields,
  shouldDisablePayerInformationFields,
} from 'selectors/ui/ui';
import { useDispatch } from 'store/configureStore';
import { useTranslations } from 'utils';
import { constraints as constraintsCV } from 'utils/clientValidate/constraints';
import { Field } from '../Field/Field';

type PayerInformationProps = {
  next: () => void;
  prev?: () => void;
  skipPaymentCreation?: boolean;
  shouldShowCaptcha?: boolean;
  onError?: (errorType: 'captcha' | 'payment') => void;
};

export function PayerInformation({
  next,
  prev,
  skipPaymentCreation = true,
  shouldShowCaptcha = true,
  onError,
}: PayerInformationProps) {
  const dispatch = useDispatch();
  const i18n = useTranslations();
  const { setError } = useModalContext();
  const captchaRef = useRef<CaptchaRef>(null);
  const hasAutoProceededRef = useRef(false);
  const [isCaptchaReady, setIsCaptchaReady] = useState(false);
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(false);

  const {
    createCheckoutPayment,
    isCreating: isCreatingPayment,
    error: paymentError,
    resetError,
  } = usePaymentCreation({
    skipPaymentCreation,
    onSuccess: () => next(),
    onError: () => onError?.('payment'),
  });

  const senderCountry = useSelector(getCurrentCountryCode);
  const isChinaSender = senderCountry === CHINA_COUNTRY_CODE;

  const rawFields = useSelector((state: RootState) => [
    getFieldById(state, 'sender_type'),
    getFieldById(state, 'sender_first_name'),
    getFieldById(state, 'sender_last_name'),
    getFieldById(state, 'sender_date_of_birth'),
    { ...getFieldById(state, 'sender_address1'), format: null },
    getFieldById(state, 'sender_address2'),
    getFieldById(state, 'sender_city'),
    getFieldById(state, 'sender_email'),
    getFieldById(state, 'sender_country'),
    getFieldById(state, 'sender_phone'),
    getFieldById(state, 'sender_state'),
  ]);

  const fields = isChinaSender ? transformChinaFields(rawFields) : rawFields;
  const fieldsToValidate = fields.filter(
    field => field.required || field.id === 'sender_address2',
  );

  const payerInformationEditableFieldsList = useSelector(
    payerInformationEditableFields,
  );
  const shouldDisableFields = useSelector(shouldDisablePayerInformationFields);
  const orderTemplate = useSelector(getOrderTemplate);
  const readOnlyFields = useSelector(getReadOnlyFields) || [];
  const suggestionInfo = useSelector(getPayerInformationCountrySuggestion);
  const isFetchingData = useSelector((state: RootState) =>
    isFetching(state, 'sender'),
  );
  const [userHasInteracted, setUserHasInteracted] = useState(false);

  const validateAllFields = (displayValidationErrors = true) =>
    fieldsToValidate.forEach(field => {
      dispatch(
        validateSectionField(field.id, {
          displayValidationErrors,
        }),
      );
    });

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding fields will cause infinite re-render
  useEffect(() => {
    const isFormFilled = fields
      .filter(field => field.required)
      .every(field => field.defaultValue !== '');

    validateAllFields(userHasInteracted || isFormFilled);
  }, [dispatch, userHasInteracted]);

  const values = fields.reduce(
    (acc, field) => ({
      ...acc,
      ...(field.defaultValue && { [field.id]: field.defaultValue }),
    }),
    {},
  );
  const constraints = constraintsCV.generate({
    fieldOrFields: fieldsToValidate,
    invalidMsg: i18n.t('errors.invalid') as string,
    requiredMsg: i18n.t('errors.required') as string,
  });

  const { isValid, errors } = useValidate(values, constraints);

  const handleNextClick = useCallback(async () => {
    if (shouldShowCaptcha && captchaRef.current?.isEnabled()) {
      await captchaRef.current?.execute();
    } else {
      await createCheckoutPayment();
    }
  }, [shouldShowCaptcha, createCheckoutPayment]);

  const handleClickNextWithToken = useCallback(
    async (token: string) => {
      if (token) {
        dispatch(setCaptchaResponse(token));
        await createCheckoutPayment();
      } else {
        onError?.('captcha');
      }
    },
    [dispatch, createCheckoutPayment, onError],
  );

  useEffect(() => {
    if (
      isValid &&
      shouldDisableFields &&
      !payerInformationEditableFieldsList.length &&
      !hasAutoProceededRef.current
    ) {
      setIsAutoAdvancing(true);

      const canAutoAdvance = shouldShowCaptcha
        ? isCaptchaReady
        : skipPaymentCreation;
      if (canAutoAdvance) {
        hasAutoProceededRef.current = true;
        handleNextClick().catch(() => {
          hasAutoProceededRef.current = false;
          setIsAutoAdvancing(false);
        });
      }
    }
  }, [
    isValid,
    shouldDisableFields,
    payerInformationEditableFieldsList.length,
    handleNextClick,
    skipPaymentCreation,
    shouldShowCaptcha,
    isCaptchaReady,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: no explanation
  useEffect(() => {
    const errorKeys = Object.keys(errors);
    if (!isValid && errorKeys.length) {
      dispatch(
        setShouldDisablePayerInformationFields({
          disablePayerInformationFields: shouldDisableFields,
          payerInformationEditableFields: errorKeys,
        }),
      );
    }
  }, [errors, isValid, dispatch, shouldDisableFields, userHasInteracted]);

  const addProps = (field: FieldModel) => ({
    ...field,
    autoComplete: `new-${field.id}`,
    readOnly: isFieldReadOnly(field),
  });

  const isFieldReadOnly = (field: FieldModel) => {
    if (field.id === SENDER_COUNTRY_FIELD) {
      return true;
    }
    return (
      !payerInformationEditableFieldsList.includes(field.id) &&
      (shouldDisableFields || readOnlyFields.includes(field.id))
    );
  };

  const fieldsWithExtraProps = fields.map(addProps);

  const [
    senderType,
    firstName,
    lastName,
    dateOfBirth,
    address,
    address2,
    city,
    email,
    country,
    phone,
    state,
  ] = fieldsWithExtraProps;

  const handleCountryChange = async (name: string, value: FieldValue) => {
    if (country.defaultValue !== value) {
      await handleFieldChange(name, value);
      await dispatch(fetchSender());
      validateAllFields();
    }
  };

  const handleChange = async (name: string, value: FieldValue) => {
    await handleFieldChange(name, value);
    if (!userHasInteracted) {
      setUserHasInteracted(true);
    }
    if (name === 'sender_type') {
      await dispatch(fetchSender());
    }
  };

  const handleFieldChange = async (name: string, value: FieldValue) => {
    await dispatch(setFieldValue(name, value));

    if (orderTemplate.id && orderTemplate.sender) {
      const orderFieldName = name.split('sender_')[1];
      let fieldNotDefined = true;

      const newFields = (orderTemplate?.sender?.fields || []).map(
        (field: FieldModel) => {
          if (field.id === orderFieldName) {
            fieldNotDefined = false;
            field.value = value;
          }
          return field;
        },
      );

      if (fieldNotDefined) {
        const newField = {
          id: orderFieldName,
          value,
          readOnly: false,
        };
        newFields.push(newField);
      }

      const payload = {
        sender: {
          fields: newFields,
        },
      };
      dispatch(updateOrderTemplate(payload));
    }
  };

  const handleBlur = (name: string) => {
    if (!userHasInteracted) {
      setUserHasInteracted(true);
    }
    dispatch(validateSectionField(name));
  };

  if (paymentError) {
    return <ErrorComponent />;
  }

  if (isCreatingPayment) {
    return <Spinner />;
  }

  return (
    <>
      {isAutoAdvancing && <Spinner />}
      {!isAutoAdvancing && (
        <div className="flex flex-col h-full" data-testid="payer-info">
          <div className="flex-1">
            {!skipPaymentCreation && prev && <BackButton onClick={prev} />}

            <Heading as="h2" size="large" className="fontWeight-semiBold">
              {i18n.t('checkout.payerInformation.title')}
            </Heading>
            {suggestionInfo && (
              <div className="mb-4 flex items-center gap-x-2 bg-red-150 p-4 align-middle text-gray-950">
                <div className="shrink-0">
                  <ExclamationIcon />
                </div>
                <div>{suggestionInfo}</div>
              </div>
            )}
            <div className="mb-8">
              {i18n.t('checkout.payerInformation.description')}
            </div>
            <div
              className="grid grid-cols-1 gap-x-2 gap-y-8 md:grid-cols-2"
              data-testid="payerInfoForm"
            >
              {senderType.required && (
                <div className="md:col-span-2 relative self-start">
                  <Field
                    field={senderType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
              )}
              <div className="relative self-start">
                <Field
                  field={firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
              <div className="relative self-start">
                <Field
                  field={lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
              <div
                className={classNames(
                  dateOfBirth.required
                    ? 'relative self-start'
                    : 'md:col-span-2 relative self-start',
                )}
              >
                <Field
                  field={email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
              {dateOfBirth.required && (
                <div className="relative self-start">
                  <Field
                    field={dateOfBirth}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
              )}
              <div className="md:col-span-2 relative self-start">
                <Field
                  field={address}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
              {(address2.required || address2.type) && (
                <div className="md:col-span-2 relative self-start">
                  <Field
                    field={address2}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
              )}
              <div className="relative self-start">
                <Field
                  field={city}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
              <div className="relative self-start">
                <Field
                  field={phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
              <div className="md:col-span-2 relative self-start">
                <Field
                  field={{ ...country, label: i18n.t('country.label') }}
                  onChange={handleCountryChange}
                />
              </div>
              {state.required && (
                <div className="md:col-span-2 relative self-start">
                  <Field
                    field={state}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="pt-6 pb-4">
            <Button
              disabled={
                (!isValid && Object.keys(errors).length > 0) || isFetchingData
              }
              onClick={handleNextClick}
              tone="primary"
              size="full"
              data-testid="next"
            >
              {i18n.t('checkout.navigation.next')}
            </Button>
          </div>
        </div>
      )}
      {shouldShowCaptcha && (
        <Captcha
          ref={captchaRef}
          onVerify={handleClickNextWithToken}
          onClose={resetError}
          onReady={() => setIsCaptchaReady(true)}
          onError={setError}
          data-testid="payerInfo-Captcha"
        />
      )}
    </>
  );
}
